import React, { useRef, useState, useEffect } from "react";
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardHeader,
  MDBCardBody,
  MDBCardFooter,
  MDBIcon,
  MDBBtn,
} from "mdb-react-ui-kit";
import DOMPurify from 'dompurify';

import { systemPrompt } from "./SysPrompt";

// ElevenLabs credentials (in .env)
const ELEVEN_API_KEY = "sk_a3fc5ae3f34c931a50d69b1ac76cf7a0295bcd2d972bacc8";
const ELEVEN_VOICE_ID = "eBvoGh8YGJn1xokno71w";

export default function App() {
  // OpenAI API Key
  const [apiKey] = useState("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");
  // Chat state
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Playback state
  const [speakingMessageId, setSpeakingMessageId] = useState(null);
  const audioRef = useRef(null);

  // --------------------------------------------------
  // Start Recording (push-to-talk)
  // --------------------------------------------------
  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        alert("Browser does not support audio recording.");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        setProcessing(true);
        const text = await transcribeWithOpenAI(blob);
        if (text) handleSendAuto(text);
      };
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error(err);
      alert("Could not start recording. Check mic permissions or HTTPS.");
    }
  };

  // Stop Recording
  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder) {
      recorder.stop();
      setIsRecording(false);
    }
  };

  // --------------------------------------------------
  // OpenAI Whisper Speech-to-Text
  // --------------------------------------------------
  const transcribeWithOpenAI = async (audioBlob) => {
    if (!apiKey) {
      alert("Please set REACT_APP_OPENAI_API_KEY in .env");
      setProcessing(false);
      return "";
    }
    const form = new FormData();
    form.append("file", audioBlob, "audio.webm");
    form.append("model", "whisper-1");
    try {
      const res = await fetch("https://api.openai.com/v1/audio/transcriptions", {
        method: "POST",
        headers: { Authorization: `Bearer ${apiKey}` },
        body: form,
      });
      const data = await res.json();
      setProcessing(false);
      if (!res.ok) {
        console.error("Whisper STT error:", data);
        return "";
      }
      return data.text || "";
    } catch (e) {
      console.error(e);
      setProcessing(false);
      return "";
    }
  };

  // --------------------------------------------------
  // ElevenLabs Text-to-Speech
  // --------------------------------------------------
  const speakWithEleven = async (text, id) => {
    // Mute current if speaking
    if (speakingMessageId !== null) {
      audioRef.current?.pause();
      setSpeakingMessageId(null);
    }
    if (!ELEVEN_API_KEY || !ELEVEN_VOICE_ID) return;
    setSpeakingMessageId(id);
    const payload = { text, model_id: "eleven_multilingual_v2" };
    try {
      const res = await fetch(
        `https://api.elevenlabs.io/v1/text-to-speech/${ELEVEN_VOICE_ID}?output_format=mp3_44100_128`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "xi-api-key": ELEVEN_API_KEY,
          },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        console.error("ElevenLabs TTS error:", await res.text());
        setSpeakingMessageId(null);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setSpeakingMessageId(null);
        URL.revokeObjectURL(url);
      };
      audio.play();
      setLoading(false)
    } catch (e) {
      console.error(e);
      setSpeakingMessageId(null);
    }
  };

  // --------------------------------------------------
  // Chat with OpenAI GPT
  // --------------------------------------------------
  const sendToOpenAI = async (userMsg) => {
    setLoading(true);
    setMessages((prev) => [...prev, userMsg]);
    try {
      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: "gpt-3.5-turbo",
          temperature: 0.9,
          messages: [{ role: "system", content: systemPrompt }, ...messages, userMsg],
        }),
      });
      const data = await res.json();
      const aiReply = data.choices?.[0]?.message;
      if (aiReply) {
        const idx = messages.length;
        speakWithEleven(aiReply.content, idx);
        setTimeout(() => setMessages((prev) => [...prev, aiReply]), 3000)

      }
    } catch (e) {
      console.error(e);
    }
  };

  // Handlers
  const handleSend = () => {
    if (!input.trim()) return;
    const userMsg = { role: "user", content: input };
    setInput("");
    sendToOpenAI(userMsg);
  };
  const handleSendAuto = (text) => {
    if (!text) return;
    const userMsg = { role: "user", content: text };
    sendToOpenAI(userMsg);
  };

  return (
    <MDBContainer fluid className="m-0 p-0" style={{ height: "100vh", backgroundColor: "#eee" }}>
      <MDBRow className="h-100 m-0">
        <MDBCol xs="12" className="h-100 p-0">
          <MDBCard style={{ height: "100%", display: "flex", flexDirection: "column", borderRadius: 0 }}>
            {/* HEADER */}
            <MDBCardHeader className="d-flex justify-content-between align-items-center p-3">
              <div className="d-flex align-items-center">
                <img src="/img/logo.jpg" alt="Logo" style={{ width: 220, height: 40, marginRight: 8 }} />
                {/* <h5 className="mb-0">CyAi</h5> */}
              </div>
              {/* <MDBBtn color="primary" size="sm" rippleColor="dark">
                Cyber AI Technologies
              </MDBBtn> */}
            </MDBCardHeader>

            {/* BODY */}
            <MDBCardBody style={{ flex: 1, overflowY: "auto" }}>
              {messages.map((msg, i) => {
                const isUser = msg.role === "user";
                const isAssistant = msg.role === "assistant";
                const isSpeaking = speakingMessageId === i;
                const sanitized = DOMPurify.sanitize(msg.content);
                return (
                  <div key={i} className={`d-flex flex-row justify-content-${isUser ? "end" : "start"} mb-4`}>
                    {isAssistant && <img src="/img/bot.png" alt="assistant avatar" style={{ width: 45, height: 45 }} />}
                    <div>
                      <p
                        className={
                          `small p-2 ${isUser
                            ? "me-3 text-white bg-primary"
                            : "ms-3"} mb-1 rounded-3`
                        }
                        style={{ whiteSpace: "pre-wrap" }}
                        dangerouslySetInnerHTML={{ __html: sanitized }}
                      />
                      {isAssistant && (
                        <button className="btn btn-link p-0 ms-3 text-muted" onClick={() => speakWithEleven(msg.content, i)}>
                          <MDBIcon fas icon={isSpeaking ? "volume-mute" : "play"} /> {isSpeaking ? "Mute" : "Play"}
                        </button>
                      )}
                    </div>
                    {isUser && <img src="/img/user.png" alt="user avatar" style={{ width: 45, height: 45 }} />}
                  </div>
                );
              })}
              {loading && (
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </MDBCardBody>

            {/* FOOTER */}
            <MDBCardFooter className="text-muted d-flex justify-content-start align-items-center p-3">
              <img src="/img/user.png" alt="avatar" style={{ width: 45, height: 45 }} />
              <input
                type="text"
                className="form-control form-control-lg"
                placeholder={processing ? "Processing..." : "Type message"}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                style={{ marginLeft: 10, marginRight: 10 }}
              />
              <MDBBtn
                color={isRecording ? "danger" : "light"}
                className="rounded-circle p-2"
                style={{ width: 48, height: 48 }}
                onMouseDown={startRecording}
                onMouseUp={stopRecording}
                onTouchStart={startRecording}
                onTouchEnd={stopRecording}
              >
                <MDBIcon fas icon={isRecording ? "microphone-slash" : "microphone"} />
              </MDBBtn>
              <MDBBtn color="light" className="ms-3" onClick={handleSend}>
                <MDBIcon fas icon="paper-plane" />
              </MDBBtn>
            </MDBCardFooter>
          </MDBCard>
        </MDBCol>
      </MDBRow>
    </MDBContainer>
  );
}
